import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';
import { zigbeeConfig } from '../config/zigbee';
import { KioskConfig, AvailableDevice } from '../types/kiosk';

// ── Översikt och kiosk ───────────────────────────────────────────────────────

/** Översikten för inloggade (samma innehåll som kiosken). */
export const fetchDashboardData = async <T = any>(): Promise<T> => {
    try { return (await axios.get<T>(`${API_BASE_URL}/dashboard/data`)).data; }
    catch (e) { return handleApiError(e, 'Hämta översikten'); }
};

/** Kioskens data med API-nyckel i stället för inloggning. Fel kastas vidare så sidan kan skilja på 401 och annat. */
export const fetchKioskData = async <T = any>(apiKey: string): Promise<T> => {
    const res = await axios.get<T>(`${API_BASE_URL}/kiosk/data`, {
        headers: { 'X-API-Key': apiKey, 'X-Tenant-Id': zigbeeConfig.tenantId },
    });
    return res.data;
};

export const fetchKioskConfig = async (): Promise<KioskConfig> => {
    try { return (await axios.get<KioskConfig>(`${API_BASE_URL}/kiosk/config`)).data; }
    catch (e) { return handleApiError(e, 'Hämta kioskinställningar'); }
};

export const fetchKioskDevices = async (): Promise<{ devices?: AvailableDevice[] }> => {
    try { return (await axios.get(`${API_BASE_URL}/kiosk/devices`)).data; }
    catch (e) { return handleApiError(e, 'Hämta enheter för kiosken'); }
};

export interface KioskKey { id: number; label: string; key_type?: string; created_at: string; last_used_at: string | null; }

/** Kiosk-nycklar (api_keys med key_type 'kiosk'). M2M-nycklarna hanteras i gatewaysApi. */
export const fetchKioskKeys = async (): Promise<KioskKey[]> => {
    try {
        const res = await axios.get<KioskKey[]>(`${API_BASE_URL}/api-keys`);
        return res.data.filter(k => k.key_type === 'kiosk');
    } catch (e) { return handleApiError(e, 'Hämta kiosk-nycklar'); }
};

/** Skapar en kiosk-nyckel. Den råa nyckeln visas bara den här gången. */
export const createKioskKey = async (label: string): Promise<{ key: string; id?: number }> => {
    try { return (await axios.post<{ key: string; id?: number }>(`${API_BASE_URL}/api-keys`, { label, keyType: 'kiosk' })).data; }
    catch (e) { return handleApiError(e, 'Skapa kiosk-nyckel'); }
};

export const deleteKioskKey = async (id: number): Promise<void> => {
    try { await axios.delete(`${API_BASE_URL}/api-keys/${id}`); }
    catch (e) { return handleApiError(e, 'Ta bort kiosk-nyckel'); }
};

export const saveKioskConfig = async (config: KioskConfig): Promise<void> => {
    try { await axios.put(`${API_BASE_URL}/kiosk/config`, config); }
    catch (e) { return handleApiError(e, 'Spara kioskinställningar'); }
};
